'use client';

import { useCallback, useEffect } from 'react';

import { imageGalleryVariants } from '@workspace/blocks/ImageGallery/imageGallery.variants';
import { cn } from '@workspace/lib';
import { buildDatUrl, buildSrcSet, resolveSource } from '@workspace/lib/picture.utils';
import { Button, ButtonIcon, Modal, Picture } from '@workspace/ui/common';

import type { ImageGalleryImage } from './image-gallery.types';

type ImageGalleryModalProps = {
  images: ImageGalleryImage[];
  activeIndex: number;
  isOpen: boolean;
  onChange: (index: number) => void;
  onClose: () => void;
};

export function ImageGalleryModal({ images, activeIndex, isOpen, onChange, onClose }: ImageGalleryModalProps) {
  const selectedIndex = Math.min(activeIndex, Math.max(0, images.length - 1));
  const selectedItem = images[selectedIndex];
  const selectedImage = selectedItem?.bynderImage;
  const linkUrl = selectedItem?.link?.href ?? selectedItem?.link?.url;
  const linkText = selectedItem?.linkText?.trim() || selectedItem?.bynderImage?.name;
  const hasMultipleImages = images.length > 1;

  useEffect(() => {
    if (!isOpen || !images.length) return;

    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    const width = isDesktop ? 1200 : 310;
    const height = isDesktop ? 675 : 175;
    const preloaders = images.flatMap((item) => {
      const image = item.bynderImage;
      if (!image) return [];

      const resolved = resolveSource(image);
      if (!resolved.baseUrl) return [];

      const preloader = new window.Image();
      preloader.decoding = 'async';

      if (resolved.isBynder && !resolved.isSvg) {
        preloader.srcset = buildSrcSet(resolved.baseUrl, width, height, true);
        preloader.sizes = `${width}px`;
        preloader.src = buildDatUrl(resolved.baseUrl, width, height);
      } else {
        preloader.src = resolved.baseUrl;
      }

      return [preloader];
    });

    return () => {
      preloaders.forEach((preloader) => {
        preloader.removeAttribute('srcset');
        preloader.removeAttribute('src');
      });
    };
  }, [images, isOpen]);

  const showPrevious = useCallback(
    () => onChange((selectedIndex - 1 + images.length) % images.length),
    [images.length, onChange, selectedIndex],
  );
  const showNext = useCallback(
    () => onChange((selectedIndex + 1) % images.length),
    [images.length, onChange, selectedIndex],
  );

  useEffect(() => {
    if (!isOpen || !hasMultipleImages) return;

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'ArrowLeft') showPrevious();
      if (event.key === 'ArrowRight') showNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasMultipleImages, isOpen, showNext, showPrevious]);

  if (!selectedItem || !selectedImage) return null;

  const alt = selectedItem.alt ?? selectedItem.bynderImage?.alt ?? '';
  const { modalContent, modalContainer, modalImage, modalLink, modalNavigationButton, modalDesktopNavigation } =
    imageGalleryVariants();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="media"
      ariaLabel="Image carousel"
      closeButtonAriaLabel="Close image carousel"
    >
      <div className={cn('relative flex flex-1 flex-col items-center px-0', modalContent())}>
        <div className={cn('flex w-full flex-col items-center', modalContainer())}>
          <div className="relative flex w-full items-center justify-center">
            <div
              className={cn(
                'relative flex max-w-[calc(100%-4rem)] items-start gap-2xs self-stretch overflow-hidden after:pointer-events-none after:absolute after:inset-0 md:max-w-full',
                modalImage(),
              )}
            >
              <Picture
                key={selectedItem._key ?? selectedIndex}
                image={selectedImage}
                alt={alt}
                desktopWidth={1200}
                desktopHeight={675}
                mobileWidth={310}
                mobileHeight={175}
                priority
                className="block size-full"
                imgClassName="size-full object-cover"
              />
            </div>

            {hasMultipleImages && (
              <>
                <ButtonIcon
                  variant="icon"
                  iconName="ChevronLeft"
                  iconSize={24}
                  aria-label="Show previous image"
                  onClick={showPrevious}
                  className={cn('absolute top-1/2 left-1.75 -translate-y-1/2 md:hidden', modalNavigationButton())}
                />
                <ButtonIcon
                  variant="icon"
                  iconName="ChevronRight"
                  iconSize={24}
                  aria-label="Show next image"
                  onClick={showNext}
                  className={cn('absolute top-1/2 right-1.75 -translate-y-1/2 md:hidden', modalNavigationButton())}
                />
              </>
            )}
          </div>

          {linkUrl && linkText && (
            <Button
              variant="text"
              overrideClasses
              url={linkUrl}
              openInNewTab={Boolean(selectedItem.link?.isOpenInNewTab)}
              className={cn('inline-flex', modalLink())}
            >
              {linkText}
            </Button>
          )}
        </div>

        {hasMultipleImages && (
          <div
            className={cn(
              'pointer-events-none fixed top-1/2 left-1/2 hidden max-w-full -translate-x-1/2 -translate-y-1/2 items-start justify-between md:flex',
              modalDesktopNavigation(),
            )}
          >
            <ButtonIcon
              variant="icon"
              iconName="ChevronLeft"
              iconSize={24}
              aria-label="Show previous image"
              onClick={showPrevious}
              className={cn('pointer-events-auto', modalNavigationButton())}
            />
            <ButtonIcon
              variant="icon"
              iconName="ChevronRight"
              iconSize={24}
              aria-label="Show next image"
              onClick={showNext}
              className={cn('pointer-events-auto', modalNavigationButton())}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
